import React, { useState, useEffect, useMemo, useRef } from 'react';
import Swal from 'sweetalert2';
import { 
  FaSearch, 
  FaChevronDown, 
  FaChevronUp, 
  FaTrash, 
  FaMoneyBillWave, 
  FaPlus, 
  FaTimes, 
  FaEdit, 
  FaFileExport 
} from 'react-icons/fa';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../../api/axiosInstance';
import Pagination from '../../Pagination/Pagination'; 
import './AddScholarship.css';

const BeneficiaryDropdown = ({ value, placeholder, scholars, searchKey, onSelect, onClear }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = scholars.filter(d => (d[searchKey] || '').toLowerCase().includes(search.toLowerCase()));

  useEffect(() => { setHighlighted(0); }, [search, open]);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKey = e => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0 && highlighted < filtered.length) { onSelect(filtered[highlighted]); setOpen(false); setSearch(''); }
    } else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className={`admin-add-scholarship-filter-trigger ${open ? 'active' : ''}`} ref={ref} onClick={() => { setOpen(o => !o); setSearch(''); }}>
      <span className="admin-add-scholarship-filter-text" style={{ color: value ? '#2c3e50' : '#334155', fontWeight: value ? '600' : '400' }}>
        {value || placeholder}
      </span>
      <div className="admin-add-scholarship-trigger-icons">
        {value && <FaTimes className="admin-add-scholarship-clear-icon" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Clear Filter" />}
        <FaChevronDown size={10} style={{ color: '#64748b' }} />
      </div>

      {open && (
        <div className="admin-add-scholarship-filter-menu" onClick={e => e.stopPropagation()}>
          <input type="text" placeholder={`Search ${placeholder}...`} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleKey} autoFocus />
          <ul ref={listRef}>
            {filtered.length > 0 ? filtered.map((d, i) => (
              <li key={d._id} className={i === highlighted ? 'admin-add-scholarship-drop-highlighted' : ''} onMouseEnter={() => setHighlighted(i)} onClick={() => { onSelect(d); setOpen(false); setSearch(''); }}>
                {d[searchKey]}
              </li>
            )) : <div className="admin-add-scholarship-drop-empty">No results found</div>}
          </ul>
        </div>
      )}
    </div>
  );
};

const SCHOLAR_TYPES = ['One Time', 'Recurring'];
const MODES = ['Cash', 'Cheque', 'UPI', 'Bank Transfer' ];
const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const AddScholarship = () => {
  const [scholars, setScholars] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedScholar, setSelectedScholar] = useState(null);
  const [editScholarshipData, setEditScholarshipData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [form, setForm] = useState({ currentWork: '', scholarType: 'One Time', amount: '', date: new Date().toISOString().split('T')[0], mode: 'Cash', refNo: '' });

  useEffect(() => { fetchScholars(); }, []);

  const fetchScholars = async () => {
    try {
      const res = await axiosInstance.get('/api/scholars');
      setScholars(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (err) { console.error(err); }
  };

  const activeScholars = useMemo(() => scholars.filter(s => !s.isDeleted), [scholars]);

  // Dynamically extract unique years from all scholarship records
  const availableYears = useMemo(() => {
    const years = [];
    scholars.forEach(s => {
      s.scholarships?.forEach(sch => {
        if (sch.date) {
          years.push(new Date(sch.date).getFullYear().toString());
        }
      });
    });
    return [...new Set(years)].sort((a, b) => b - a);
  }, [scholars]);

  const filteredScholarships = useMemo(() => {
    let list = [];
    scholars.forEach(s => {
      s.scholarships?.forEach(sch => {
        list.push({ 
          ...sch, 
          scholarId: s._id, 
          name: s.name, 
          fatherName: s.fatherName, 
          contactNumber: s.contactNumber, 
          isActive: sch.isActive !== false
        });
      });
    });

    // Apply Year Filter
    if (yearFilter) {
      list = list.filter(sch => sch.date && new Date(sch.date).getFullYear().toString() === yearFilter);
    }

    // Apply Scholarship Type Filter
    if (typeFilter) {
      list = list.filter(sch => sch.scholarType === typeFilter);
    }

    // Apply Payment Mode Filter
    if (modeFilter) {
      list = list.filter(sch => sch.mode === modeFilter);
    }

    // Apply Search Filter
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      list = list.filter(l => 
        (l.name || '').toLowerCase().includes(q) || 
        (l.fatherName || '').toLowerCase().includes(q) || 
        (l.contactNumber || '').includes(q)
      );
    }
    
    // Sort logic to ensure newest (latest) show first
    return list.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      
      if (a._id && b._id) {
        return b._id.toString().localeCompare(a._id.toString());
      }
      return 0;
    });
  }, [scholars, searchFilter, yearFilter, typeFilter, modeFilter]);

  // Reset pagination when any filter changes
  useEffect(() => { 
    setCurrentPage(1); 
  }, [searchFilter, yearFilter, typeFilter, modeFilter]);

  const paginatedScholarships = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredScholarships.slice(start, start + itemsPerPage);
  }, [filteredScholarships, currentPage, itemsPerPage]);

  // Helper check for active recurring scholarship duplicates
  const checkExistingRecurring = (scholar, selectedType) => {
    if (selectedType === 'Recurring' && scholar && !editScholarshipData) {
      const hasRecurring = scholar.scholarships?.some(s => s.scholarType === 'Recurring' && s.isActive !== false);
      if (hasRecurring) {
        Swal.fire({
          title: 'Already Registered',
          text: 'This beneficiary already has an active Recurring scholarship. Please disburse a "One Time" grant, or process renewals through the Renewal Reminder List.',
          icon: 'warning',
          confirmButtonColor: '#e67e22'
        });
        return true;
      }
    }
    return false;
  };

  const handleScholarSelect = (scholar) => {
    setSelectedScholar(scholar);
    // Trigger immediate validation if "Recurring" option was already selected
    if (form.scholarType === 'Recurring') {
      const isDuplicate = checkExistingRecurring(scholar, 'Recurring');
      if (isDuplicate) {
        setForm(prev => ({ ...prev, scholarType: 'One Time' }));
      }
    }
  };

  const handleTypeChange = (type) => {
    if (type === 'Recurring' && selectedScholar) {
      const isDuplicate = checkExistingRecurring(selectedScholar, 'Recurring');
      if (isDuplicate) {
        // Reset type selection immediately if duplicate is active
        setForm(prev => ({ ...prev, scholarType: 'One Time' }));
        return;
      }
    }
    setForm(prev => ({ ...prev, scholarType: type }));
  };
  
  const handleClearScholar = () => { 
    setSelectedScholar(null); 
    setEditScholarshipData(null);
    setForm({ currentWork: '', scholarType: 'One Time', amount: '', date: new Date().toISOString().split('T')[0], mode: 'Cash', refNo: '' }); 
  };

  const handleAmountChange = (e) => {
    const onlyDigits = e.target.value.replace(/\D/g, ''); 
    setForm({ ...form, amount: onlyDigits });
  };

  const handleProcessScholarship = async () => {
    if (!selectedScholar) return Swal.fire('Warning', 'Please select a beneficiary first.', 'warning');
    if (!form.currentWork || !form.amount || !form.date) return Swal.fire('Warning', 'Current Work, Amount and Date are required.', 'warning');

    try {
      if (editScholarshipData) {
        await axiosInstance.put(`/api/scholars/${editScholarshipData.scholarId}/scholarships/${editScholarshipData.scholarshipId}`, form);
        Swal.fire('Updated', 'Scholarship record updated successfully', 'success');
      } else {
        await axiosInstance.post(`/api/scholars/${selectedScholar._id}/scholarships`, form);
        Swal.fire('Success', 'Scholarship added successfully', 'success');
      }
      handleClearScholar(); setShowAddForm(false); fetchScholars();
    } catch (err) { Swal.fire('Error', err.response?.data?.message || 'Failed to process scholarship', 'error'); }
  };

  const handleEditScholarship = (sch) => {
    const scholar = scholars.find(s => s._id === sch.scholarId);
    setSelectedScholar(scholar);
    setForm({
      currentWork: sch.currentWork || '', scholarType: sch.scholarType || 'One Time',
      amount: sch.amount ? sch.amount.toString() : '', date: sch.date ? new Date(sch.date).toISOString().split('T')[0] : '',
      mode: sch.mode || 'Cash', refNo: sch.refNo || ''
    });
    setEditScholarshipData({ scholarId: sch.scholarId, scholarshipId: sch._id });
    setShowAddForm(true); window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteScholarship = async (scholarId, scholarshipId) => {
    const res = await Swal.fire({ title: 'Remove Record?', text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' });
    if (res.isConfirmed) { await axiosInstance.delete(`/api/scholars/${scholarId}/scholarships/${scholarshipId}`); fetchScholars(); }
  };

  const toggleScholarshipStatus = async (scholarId, scholarshipId, currentStatus) => {
    try {
      await axiosInstance.put(`/api/scholars/${scholarId}/scholarships/${scholarshipId}/status`, { isActive: !currentStatus });
      fetchScholars();
    } catch (err) {
      Swal.fire("Error", "Could not update status", "error");
    }
  };

  const handleExportExcel = async () => {
    if (filteredScholarships.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Scholarship Records');

    // Title 
    worksheet.mergeCells('A1:J1');
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    // Subtitle
    worksheet.mergeCells('A2:J2');
    worksheet.getCell('A2').value = 'Beneficiary Scholarship Disbursement Report';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    // Applied Filters Metadata
    worksheet.mergeCells('A3:J3');
    const appliedYearText = yearFilter ? yearFilter : 'All Years';
    const appliedTypeText = typeFilter ? typeFilter : 'All Types';
    const appliedModeText = modeFilter ? modeFilter : 'All Modes';
    const appliedSearchText = searchFilter.trim() ? `"${searchFilter.trim()}"` : 'None';
    worksheet.getCell('A3').value = `Filters — Year: ${appliedYearText} | Type: ${appliedTypeText} | Mode: ${appliedModeText} | Search: ${appliedSearchText}`;
    worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF555555' } };
    worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]); // Blank spacer row

    // Table Header Row (includes Father's Name)
    const headerRow = worksheet.addRow(['S.No', 'Date Granted', 'Beneficiary Name', "Father's Name", 'Contact Number', 'Class/Work', 'Scholarship Type', 'Amount (₹)', 'Payment Details', 'Status']);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Populate Data Rows
    filteredScholarships.forEach((sch, i) => {
      const formattedDate = new Date(sch.date).toLocaleDateString('en-IN');
      const paymentInfo = `${sch.mode} ${sch.refNo ? `(${sch.refNo})` : ''}`;
      
      const row = worksheet.addRow([
        i + 1,
        formattedDate,
        sch.name,
        sch.fatherName || 'N/A', // Father's Name column added
        sch.contactNumber,
        sch.currentWork,
        sch.scholarType,
        Number(sch.amount),
        paymentInfo,
        sch.isActive ? 'Active' : 'Disabled'
      ]);

      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      });

      row.getCell(1).alignment = { horizontal: 'center' };
      row.getCell(2).alignment = { horizontal: 'center' };
      row.getCell(5).alignment = { horizontal: 'center' };
      row.getCell(7).alignment = { horizontal: 'center' };
      row.getCell(8).alignment = { horizontal: 'right' };
      row.getCell(8).numFmt = '₹#,##0';
      
      const statusCell = row.getCell(10);
      statusCell.alignment = { horizontal: 'center' };
      statusCell.font = { bold: true, color: { argb: sch.isActive ? 'FF16A34A' : 'FFDC2626' } };
    });

    // Adjust Columns Width for Excel Output
    worksheet.columns = [
      { width: 8 },  // S.No
      { width: 15 }, // Date Granted
      { width: 25 }, // Beneficiary Name
      { width: 25 }, // Father's Name
      { width: 18 }, // Contact Number
      { width: 25 }, // Class/Work
      { width: 18 }, // Scholarship Type
      { width: 15 }, // Amount
      { width: 30 }, // Payment Details
      { width: 15 }  // Status
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Scholarship_Disbursements_Report.xlsx');
  };

  return (
    <>
    <Helmet>
                <title>Grant Scholarship | Rajput Shiksha Kosh</title>
        
                <meta
                  name="description"
                  content="Manage scholarship grants for the Rajput Shiksha Kosh organization."
                />
              </Helmet>
    <div className="admin-add-scholarship-container">
      <h2 className="admin-add-scholarship-title"><FaMoneyBillWave style={{ marginRight: '8px' }} /> Grant Scholarship</h2>
      
      <div className="admin-add-scholarship-controls">
        <div className="admin-add-scholarship-search-wrapper">
          <FaSearch className="admin-add-scholarship-search-icon" />
          <input type="text" className="admin-add-scholarship-search-input" placeholder="Search by Beneficiary, Father, or Contact..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
        </div>
        
        <div className="admin-add-scholarship-action-group">
          {/* Year Filter */}
          <select 
            className="admin-add-scholarship-limit-select" 
            value={yearFilter} 
            onChange={(e) => setYearFilter(e.target.value)}
          >
            <option value="">Filter by Year (All)</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          {/* Scholarship Type Filter */}
          <select 
            className="admin-add-scholarship-limit-select" 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Filter by Type (All)</option>
            {SCHOLAR_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          {/* Payment Mode Filter */}
          <select 
            className="admin-add-scholarship-limit-select" 
            value={modeFilter} 
            onChange={(e) => setModeFilter(e.target.value)}
          >
            <option value="">Filter by Mode (All)</option>
            {MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          {/* Items Per Page */}
          <select className="admin-add-scholarship-limit-select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} / page</option>)}
          </select>

          {/* Export to Excel */}
          <button className="admin-add-scholarship-btn" onClick={handleExportExcel} title="Export to Excel">
            <FaFileExport /> Export
          </button>

          {/* Grant New Button */}
          <button className="admin-add-scholarship-btn admin-add-scholarship-btn-add" onClick={() => { setShowAddForm(!showAddForm); handleClearScholar(); }}>
            {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Grant New</>}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="admin-add-scholarship-inline-form">
          <h3 className="admin-add-scholarship-form-title">
            {editScholarshipData ? '✏️ Edit Scholarship Record' : 'Record New Beneficiary Scholarship Details'}
          </h3>
          
          <h4 className="admin-add-scholarship-section-title">Step 1: Select Beneficiary (Choose any)</h4>
          <div className="admin-add-scholarship-form-grid">
            <div className="admin-add-scholarship-form-group">
              <label>Select By Name</label>
              <BeneficiaryDropdown value={selectedScholar?.name || ''} placeholder="Search Name" scholars={activeScholars} searchKey="name" onSelect={handleScholarSelect} onClear={handleClearScholar} />
            </div>
            <div className="admin-add-scholarship-form-group">
              <label>Select By Father Name</label>
              <BeneficiaryDropdown value={selectedScholar?.fatherName || ''} placeholder="Search Father Name" scholars={activeScholars} searchKey="fatherName" onSelect={handleScholarSelect} onClear={handleClearScholar} />
            </div>
            <div className="admin-add-scholarship-form-group">
              <label>Select By Contact</label>
              <BeneficiaryDropdown value={selectedScholar?.contactNumber || ''} placeholder="Search Contact" scholars={activeScholars} searchKey="contactNumber" onSelect={handleScholarSelect} onClear={handleClearScholar} />
            </div>
          </div>

          <h4 className="admin-add-scholarship-section-title">Step 2: Scholarship Details</h4>
          <div className="admin-add-scholarship-form-grid">
            <div className="admin-add-scholarship-form-group admin-add-scholarship-col-span-full">
              <label>Scholarship Type <span className="admin-add-scholarship-req">*</span></label>
              <div className="admin-add-scholarship-radios">
                {SCHOLAR_TYPES.map(type => (
                  <label key={type} className={`admin-add-scholarship-radio-label ${form.scholarType === type ? 'selected' : ''}`}>
                    <input type="radio" value={type} checked={form.scholarType === type} onChange={e => handleTypeChange(e.target.value)} />
                    {type}
                  </label>
                ))}
              </div>
            </div>
            <div className="admin-add-scholarship-form-group">
              <label>Current Work / Class <span className="admin-add-scholarship-req">*</span></label>
              <input type="text" value={form.currentWork} onChange={e => setForm({ ...form, currentWork: e.target.value })} placeholder="e.g. B.Tech 2nd Year" />
            </div>
            
            <div className="admin-add-scholarship-form-group">
              <label>Amount (₹) <span className="admin-add-scholarship-req">*</span></label>
              <input type="text" value={form.amount} onChange={handleAmountChange} placeholder="Enter Amount" />
            </div>

            <div className="admin-add-scholarship-form-group">
              <label>Date Granted <span className="admin-add-scholarship-req">*</span></label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="admin-add-scholarship-form-group">
              <label>Payment Mode <span className="admin-add-scholarship-req">*</span></label>
              <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {form.mode !== 'Cash' && (
              <div className="admin-add-scholarship-form-group">
                <label>Ref / Txn No <span className="admin-add-scholarship-req">*</span></label>
                <input type="text" value={form.refNo} onChange={e => setForm({ ...form, refNo: e.target.value })} placeholder="Txn Details" />
              </div>
            )}
          </div>

          <div className="admin-add-scholarship-form-buttons">
            <button className="admin-add-scholarship-btn-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
            <button className="admin-add-scholarship-btn-confirm" onClick={handleProcessScholarship}>
              {editScholarshipData ? 'Update Record' : 'Submit Record'}
            </button>
          </div>
        </div>
      )}

      <div className="admin-add-scholarship-table-wrapper">
        <table className="admin-add-scholarship-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th className="admin-add-scholarship-col-date">Date</th>
              <th className="admin-add-scholarship-align-left">Beneficiary Info</th>
              <th className="admin-add-scholarship-col-desk">Class/Work</th>
              <th className="admin-add-scholarship-col-desk">Type</th>
              <th className="admin-add-scholarship-col-amount">Amount</th>
              <th className="admin-add-scholarship-col-desk">Mode & Ref</th>
              <th className="admin-add-scholarship-col-status">Status</th>
              <th className="admin-add-scholarship-col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedScholarships.length === 0 ? <tr><td colSpan="9" style={{padding: '40px', textAlign: 'center', color: '#64748b'}}>No records found.</td></tr> : 
              paginatedScholarships.map((sch, i) => {
                const globalIndex = (currentPage - 1) * itemsPerPage + i;
                return (
                  <React.Fragment key={sch._id}>
                    <tr className="admin-add-scholarship-main-row">
                      <td>{globalIndex + 1}</td>
                      <td className="admin-add-scholarship-col-date" style={{fontWeight: 500}}>{new Date(sch.date).toLocaleDateString('en-IN')}</td>
                      <td className="admin-add-scholarship-align-left">
                        <div className="admin-add-scholarship-name-wrapper" onClick={() => setExpandedRow(expandedRow === sch._id ? null : sch._id)}>
                          <div><span style={{fontWeight: 600}}>{sch.name}</span><br/><span style={{fontSize: '0.8rem', color: '#64748b'}}>{sch.contactNumber}</span></div>
                          <button className="admin-add-scholarship-expand-btn">{expandedRow === sch._id ? <FaChevronUp /> : <FaChevronDown />}</button>
                        </div>
                      </td>
                      <td className="admin-add-scholarship-col-desk">{sch.currentWork}</td>
                      <td className="admin-add-scholarship-col-desk"><span className="admin-add-scholarship-badge">{sch.scholarType}</span></td>
                      <td className="admin-add-scholarship-col-amount" style={{fontWeight: 700, color: '#e67e22'}}>₹{Number(sch.amount).toLocaleString('en-IN')}</td>
                      <td className="admin-add-scholarship-col-desk" style={{fontSize: '0.85rem'}}><b>{sch.mode}</b> {sch.refNo ? `(${sch.refNo})` : ''}</td>
                      
                      {/* Desktop Status Toggle */}
                      <td className="admin-add-scholarship-col-status">
                        <label className="admin-add-scholarship-switch" title={sch.isActive ? "Active" : "Inactive"}>
                          <input type="checkbox" checked={sch.isActive} onChange={() => toggleScholarshipStatus(sch.scholarId, sch._id, sch.isActive)} />
                          <span className="admin-add-scholarship-slider round"></span>
                        </label>
                        <div style={{ fontSize: '0.75rem', marginTop: '4px', color: sch.isActive ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                          {sch.isActive ? 'Active' : 'Disabled'}
                        </div>
                      </td>

                      <td className="admin-add-scholarship-col-action">
                        <div className="admin-add-scholarship-action-btns">
                          <button className="admin-add-scholarship-btn-edit" onClick={() => handleEditScholarship(sch)} title="Edit"><FaEdit /></button>
                          <button className="admin-add-scholarship-btn-delete" onClick={() => deleteScholarship(sch.scholarId, sch._id)} title="Delete"><FaTrash /></button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Row for Mobile/Tablet (< 1200px) */}
                    {expandedRow === sch._id && (
                      <tr className="admin-add-scholarship-expanded-row">
                        <td colSpan="9">
                          <div className="admin-add-scholarship-expanded-content">
                            <div className="admin-add-scholarship-exp-item admin-add-scholarship-exp-desk"><span className="admin-add-scholarship-exp-label">Type</span><span className="admin-add-scholarship-exp-value">{sch.scholarType}</span></div>
                            <div className="admin-add-scholarship-exp-item admin-add-scholarship-exp-amount"><span className="admin-add-scholarship-exp-label">Amount</span><span className="admin-add-scholarship-exp-value" style={{color: '#e67e22'}}>₹{Number(sch.amount).toLocaleString('en-IN')}</span></div>
                            <div className="admin-add-scholarship-exp-item admin-add-scholarship-exp-desk"><span className="admin-add-scholarship-exp-label">Mode & Ref</span><span className="admin-add-scholarship-exp-value">{sch.mode} {sch.refNo}</span></div>
                            <div className="admin-add-scholarship-exp-item admin-add-scholarship-col-span-full"><span className="admin-add-scholarship-exp-label">Class/Work</span><span className="admin-add-scholarship-exp-value">{sch.currentWork}</span></div>
                            
                            {/* Status Toggle inside Expanded View for responsiveness */}
                            <div className="admin-add-scholarship-exp-item admin-add-scholarship-exp-status">
                               <span className="admin-add-scholarship-exp-label">Status</span>
                               <div className="admin-add-scholarship-exp-value" style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                  <label className="admin-add-scholarship-switch" title={sch.isActive ? "Active" : "Inactive"}>
                                    <input type="checkbox" checked={sch.isActive} onChange={() => toggleScholarshipStatus(sch.scholarId, sch._id, sch.isActive)} />
                                    <span className="admin-add-scholarship-slider round"></span>
                                  </label>
                                  <span style={{ fontSize: '0.85rem', color: sch.isActive ? '#e67e22' : '#94a3b8', fontWeight: 'bold' }}>
                                    {sch.isActive ? 'Active' : 'Disabled'}
                                  </span>
                               </div>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            }
          </tbody>
        </table>
      </div>

      {filteredScholarships.length > 0 && (
        <Pagination totalItems={filteredScholarships.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
      )}

    </div>
    </>
  );
};

export default AddScholarship;